import React, { useState, useRef, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import CalendarView from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/prescriptionMakingPageComponents/NextVisitSlotSelector/components/CalendarView'
import CustomSlotHolder from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/prescriptionMakingPageComponents/NextVisitSlotSelector/components/CustomSlotHolder'
import SidewaysDateSelector from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/prescriptionMakingPageComponents/SidewaysDateSelector'
import { NextVisitSlotSelectorContainer } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/styledComponents'
import { getDataWithParams } from 'src/consumer/services/profileService'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

export default function SlotSelectionView({
  bookingDate,
  selectedDate,
  setSelectedDate,
  onSlotSelect,
  currentEventTypeObject,
  mentorProfile,
  currentBookedEventId,
  clinicLocation = null, // Clinic location for timezone
  bookedSlots = [], // Booked slots to disable overlapping slots
  bookedSlotsLoading = false, // Loading state for booked slots
}) {
  const [currentTab, setSelectedCurrentTab] = useState('allSlots')
  const containerRef = useRef(null)
  const scrollPositionRef = useRef(0)
  const isScrollingRef = useRef(false)
  
  // Lazy load slots for new dates in the background
  const handleDatesLoaded = useCallback(async (startDate, endDate) => {
    if (!mentorProfile?.id || !currentEventTypeObject) {
      return
    }

    try {
      // Fetch slots for the new date range in the background
      const startTime = dayjs.isDayjs(startDate) ? startDate.startOf('day').toISOString() : dayjs(startDate).startOf('day').toISOString()
      const endTime = dayjs.isDayjs(endDate) ? endDate.endOf('day').toISOString() : dayjs(endDate).endOf('day').toISOString()
      const zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'

      // Fetch in background - don't block UI
      getDataWithParams(
        `/api/secure/mentor/available-slots`,
        {
          mentorId: mentorProfile.id,
          startTime: startTime,
          endTime: endTime,
          eventTypeId: currentEventTypeObject.uid || currentEventTypeObject.id,
          eventType: currentEventTypeObject.type,
          zoneId: zoneId,
        },
      ).then((response) => {
        if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
          // Slots are now cached/preloaded for when user selects those dates
          // The TimeSlotGrid will fetch them again when needed, but they might be cached
          console.log(`Preloaded slots for date range: ${startDate.format('DD MMM')} - ${endDate.format('DD MMM')}`)
        }
      }).catch((error) => {
        console.error('Error preloading slots:', error)
        // Fail silently - slots will be loaded when user selects those dates
      })
    } catch (error) {
      console.error('Error in handleDatesLoaded:', error)
      // Fail silently
    }
  }, [mentorProfile, currentEventTypeObject])

  // Track scroll position
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      if (!isScrollingRef.current) {
        scrollPositionRef.current = container.scrollTop
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Restore scroll position after updates, but only if user wasn't actively scrolling
  useEffect(() => {
    const container = containerRef.current
    if (container && scrollPositionRef.current > 0 && !isScrollingRef.current) {
      // Use a small delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        if (container && scrollPositionRef.current > 0) {
          isScrollingRef.current = true
          container.scrollTop = scrollPositionRef.current
          // Reset flag after scroll restoration
          requestAnimationFrame(() => {
            isScrollingRef.current = false
          })
        }
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  })

  return (
    <NextVisitSlotSelectorContainer ref={containerRef}>
      <SidewaysDateSelector
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        minDate={bookingDate}
        maxDate={bookingDate.add(12, 'month')}
        daysToShow={365}
        bookingDate={bookingDate}
        onDatesLoaded={handleDatesLoaded}
      />
      <div className="py-2">
        <BaseUITabsComponent
          tabs={[
            { id: 'allSlots', title: 'Select a time slot to hold' },
            { id: 'customSlot', title: 'Custom Slot' },
          ]}
          value={currentTab}
          onChange={setSelectedCurrentTab}
          getTabId={(tab) => tab?.id}
          getTabLabel={(tab) => tab?.title}
          getTabIcon={(tab) => tab?.icon}
          incicatorColor="primary"
          alignContentInColumnForm={false}
          variant="underlined"
          unselectedColor="#000"
          selectedColor="#000"
          hoverItemColor="#000"
        />
      </div>

      {currentTab === 'allSlots' && (
        <CalendarView
          bookingDate={bookingDate}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onSlotSelect={onSlotSelect}
          currentEventTypeObject={currentEventTypeObject}
          mentorProfile={mentorProfile}
          currentBookedEventId={currentBookedEventId}
          clinicLocation={clinicLocation}
          bookedSlots={bookedSlots}
          bookedSlotsLoading={bookedSlotsLoading}
        />
      )}
      {currentTab === 'customSlot' && (
        <CustomSlotHolder
          onSlotSelect={onSlotSelect}
          selectedDate={selectedDate}
          mentorProfile={mentorProfile}
          duration={currentEventTypeObject?.durationInMins || 30}
          clinicLocation={clinicLocation}
        />
      )}
    </NextVisitSlotSelectorContainer>
  )
}
